import classnames from "classnames";
import {useEffect} from "react";

import Button from "@/components/button/Button.tsx";
import {Loader} from "@/components/placeholder/Loader.tsx";
import {shortCurrency} from "@/lib/format.tsx";

import styles from "./AddonsSummary.module.scss";

export interface AddonsSummaryProps {
  basePrice: number;
  addedCost: number;
  addedCostFull: number;
  isPastDue: boolean;
  isMaxHit: boolean;
  isInvoiced: boolean;
  accountCanPurchase: boolean;
  userCanPurchase: boolean;
  onPurchase: () => void;
  onQuote: () => void;
}

export const AddonsSummary = ({
  basePrice,
  addedCost,
  addedCostFull,
  isPastDue,
  isMaxHit,
  isInvoiced,
  accountCanPurchase,
  userCanPurchase,
  onPurchase,
  onQuote,
}: AddonsSummaryProps) => {
  const isQuote = !userCanPurchase || !accountCanPurchase || isMaxHit;
  let quoteHelp = "Order exceeds self-serve limits and will be sent to your Account Rep for processing.";
  if (!userCanPurchase || !accountCanPurchase) {
    quoteHelp = "You do not have permissions to add features";
    if (!accountCanPurchase) {
      quoteHelp =
        quoteHelp + " until a valid payment method and subscription is added to your account by your account owner";
    }
  }
  if (isInvoiced) {
    quoteHelp = "*Invoiced & Custom Plans must contact Uptime.com to upgrade";
  }
  // pull up Zendesk Help button to not cover up the Next/Get Quote button
  useEffect(() => {
    document.getElementById("launcher")?.classList.add(styles.pulledUp);
    return () => {
      document.getElementById("launcher")?.classList.remove(styles.pulledUp);
    };
  }, []);
  return (
    <div
      className={classnames(
        styles.PriceStickyBanner,
        "white-block white-block-border d-flex align-items-center justify-content-between"
      )}
    >
      {isPastDue ? <small className="fs-7 fw-semibold">Subscription Payment Past Due</small> : null}
      {isQuote && !isPastDue && <small className="text-muted fs-7">{quoteHelp}</small>}
      {!isQuote && !isPastDue && (
        <div>
          {!addedCost ? (
            <Loader text="…" />
          ) : (
            <>
              <div className="fs-4 lh-sm fw-medium mb-2">
                {Math.round(addedCostFull) > Math.round(addedCost) && (
                  <span className="text-decoration-line-through pulse-color-gray-500">
                    {shortCurrency(addedCostFull)}
                  </span>
                )}
                <span>{shortCurrency(addedCost)}</span>/mo
              </div>
              <div className="pulse-color-gray-500">
                <small>
                  Current Plan: <strong>{shortCurrency(basePrice)}</strong> +{" "}
                  <strong>{shortCurrency(addedCost)}</strong> Added Cost
                </small>
              </div>
            </>
          )}
        </div>
      )}
      <>
        {addedCostFull > 0 && (
          <Button
            size={"large"}
            onClick={() => {
              if (isQuote || isInvoiced) {
                onQuote();
              } else {
                onPurchase();
              }
            }}
          >
            {isQuote ? "Get Quote" : "Next"}
          </Button>
        )}
      </>
    </div>
  );
};
